// One media element for the whole call: Safari grants playback permission per
// element, so unlocking three independent tones after async call setup fails.
// HTML media also uses iOS's media playback path, unlike Ambient native effects
// and Web Audio (which can respect the ringer switch).
import { captureException } from "@sentry/react";
// Bundle the short startup cues so a cold media request cannot delay a fast call.
import callingSoundFile from "../sounds/callingSound.mp3?inline";
import callAnsweredSoundFile from "../sounds/callAnsweredSound.mp3?inline";
import callHangUpSoundFile from "../sounds/callHangUpSound.mp3";

export type CallTone = "calling" | "answered" | "hangup";
const files: Record<CallTone, string> = {
  calling: callingSoundFile, answered: callAnsweredSoundFile, hangup: callHangUpSoundFile,
};
// One silent PCM sample. Start unmuted media during the gesture without playing
// a call cue before microphone permission or bypassing the sound preference.
const SILENCE = "data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQIAAAAAAA==";
let player: HTMLAudioElement | null = null;
let unlocked = false;
let playbackGeneration = 0;
let activeTone: CallTone | null = null;

function getPlayer(): HTMLAudioElement | null {
  if (typeof Audio === "undefined") return null;
  if (!player) {
    player = new Audio(SILENCE);
    player.preload = "auto";
  }
  return player;
}

/** Runs synchronously in the call-entry gesture, before mic/token/network awaits. */
export function unlockVoiceCallAudio(): void {
  if (unlocked) return;
  const audio = getPlayer();
  if (!audio) return;
  const generation = playbackGeneration;
  if (activeTone === null) { audio.src = SILENCE; audio.loop = false; }
  void audio.play().then(() => {
    unlocked = true;
    // A delayed priming completion must never pause a call that already began.
    if (generation === playbackGeneration && activeTone === null) audio.pause();
  }).catch(() => { /* A later gesture can retry. */ });
}

export function stopVoiceCallingAudio(): void {
  if (activeTone !== "calling") return;
  playbackGeneration += 1;
  activeTone = null;
  player?.pause();
  if (player) player.currentTime = 0;
}

/** False means no media support; callers may use the existing native bridge. */
export function playVoiceCallAudio(tone: CallTone, onPlaybackFailure?: () => void): boolean {
  const audio = getPlayer();
  if (!audio) return false;
  if (tone === "calling" && activeTone === tone && !audio.paused) return true;
  const generation = ++playbackGeneration;
  activeTone = tone;
  audio.pause();
  audio.src = files[tone];
  audio.loop = tone === "calling";
  audio.currentTime = 0;
  audio.onended = () => { if (generation === playbackGeneration) activeTone = null; };
  void audio.play().then(() => { unlocked = true; }).catch((error) => {
    // Changing source/stopping while play() is pending is expected on fast
    // connect/cancel. A rejected play must not restart an obsolete ringtone.
    if (generation !== playbackGeneration || error?.name === "AbortError") return;
    // A failed one-shot must not become the next gesture's priming sound.
    activeTone = null;
    unlocked = false;
    onPlaybackFailure?.();
    captureException(error, {
      tags: { feature: "voice", operation: "call-tone-playback" },
      extra: { tone },
    });
  });
  return true;
}
